/**
 * SDK-managed chat. `@ai-sdk/react` `useChat` owns the chat state
 * machine; we add only a thin bridge:
 *  - HTTP+SSE transport to the stateless agent server,
 *  - the `onToolCall` handler: the read tool auto-runs; the mutating
 *    tool is routed through the proposal gate into a PROPOSAL (no tool output
 *    until the clinician Applies/Rejects — structural HITL),
 *  - `sendAutomaticallyWhen` so the loop resumes once a tool output is
 *    supplied (read auto-output, or the clinician's Apply/Reject outcome),
 *  - the engine-bound ports from the production adapter,
 *  - ≤1-pending tracked client-side (defence-in-depth).
 *
 * `stop()` aborts the in-flight stream (drawer close / Stop button).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useChat } from '@ai-sdk/react';
import {
    DefaultChatTransport,
    lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import AppConfig from '~/utils/appconfig';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    handleAgentToolCall,
    mutatingOpHandler,
    projectListedStructures,
    type AddToolOutputArgs,
    type MutatingOpHandler,
    type ReadOpHandler,
} from '../ops/core';
import {
    APPLY_SYMMETRIC_MARGIN_TOOL_NAME,
    CREATE_RING_TOOL_NAME,
    APPLY_ASYMMETRIC_MARGIN_TOOL_NAME,
    BOOLEAN_OPERATION_TOOL_NAME,
    ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME,
    PROPOSE_CONTOUR_MODEL_TOOL_NAME,
    START_AUTO_SEGMENTATION_TOOL_NAME,
    SEARCH_PATIENTS_TOOL_NAME,
    LIST_PATIENT_STUDIES_TOOL_NAME,
    PROPOSE_LOAD_SERIES_TOOL_NAME,
    LIST_IMPORTED_PATIENTS_TOOL_NAME,
    CHECK_PACS_CONNECTION_TOOL_NAME,
    FILL_CAVITIES_TOOL_NAME,
    REMOVE_SMALL_PARTS_TOOL_NAME,
    CLEAR_CONTOURS_TOOL_NAME,
    ZOOM_VIEW_TOOL_NAME,
    CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME,
    CANCEL_AUTO_SEGMENTATION_TOOL_NAME,
    SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME,
    SET_STRUCTURE_VISIBILITY_TOOL_NAME,
    GO_TO_POSITION_TOOL_NAME,
    SET_WINDOW_LEVEL_TOOL_NAME,
    SET_STRUCTURE_COLOR_TOOL_NAME,
    INTERPOLATE_STRUCTURE_TOOL_NAME,
    SMOOTH_STRUCTURE_TOOL_NAME,
    MOVE_STRUCTURE_TOOL_NAME,
    RENAME_STRUCTURE_TOOL_NAME,
    DELETE_STRUCTURE_TOOL_NAME,
    DUPLICATE_STRUCTURE_TOOL_NAME,
    REPLACE_STRUCTURE_CONTOURS_TOOL_NAME,
    SET_STRUCTURE_TYPE_TOOL_NAME,
    CREATE_MARKER_TOOL_NAME,
    SET_MARKER_POSITION_TOOL_NAME,
    CREATE_STRUCTURE_SET_TOOL_NAME,
    EDIT_STRUCTURE_SET_INFO_TOOL_NAME,
    SET_STRUCTURE_SET_APPROVAL_TOOL_NAME,
    GET_STRUCTURE_PROPERTIES_TOOL_NAME,
    RESET_DISPLAY_TOOL_NAME,
    SHOW_IN_VIEWER_TOOL_NAME,
    LIST_TIMEPOINTS_TOOL_NAME,
    BRING_PRIOR_CONTOUR_TOOL_NAME,
    COMPARE_STRUCTURES_TOOL_NAME,
} from '../ops/core';
import {
    analyzeImageForModelSelection,
    planProposeContourModel,
    planStartAutoSegmentation,
    useModelSelectionDeps,
    type ModelProposal,
} from '../ops/modelSelection';
import {
    checkPacsConnection,
    listImportedPatients,
    listPatientStudies,
    planProposeLoadSeries,
    searchPatients,
    usePatientDeps,
    type LoadSeriesProposal,
} from '../ops/patients';
import { planMarginToolCall, type MarginProposal } from '../ops/margin';
import { planRingToolCall, type RingProposal } from '../ops/ring';
import {
    planAsymMarginToolCall,
    type AsymMarginProposal,
} from '../ops/asymMargin';
import { planBooleanToolCall, type BooleanProposal } from '../ops/boolean';
import { useMarginEngineDeps } from '../ops/margin';
import { useRingEngineDeps } from '../ops/ring';
import { useAsymMarginEngineDeps } from '../ops/asymMargin';
import { useBooleanEngineDeps } from '../ops/boolean';
import {
    planFillCavitiesToolCall,
    useFillCavitiesEngineDeps,
    type FillCavitiesProposal,
} from '../ops/fillCavities';
import {
    planRemoveSmallPartsToolCall,
    useRemoveSmallPartsEngineDeps,
    type RemoveSmallPartsProposal,
} from '../ops/removeSmallParts';
import {
    planClearContoursToolCall,
    useClearContoursEngineDeps,
    type ClearContoursProposal,
} from '../ops/clearContours';
import { useZoomViewDeps, zoomView } from '../ops/zoomView';
import {
    checkAutoSegmentationJobs,
    planCancelAutoSegmentation,
    useContourJobDeps,
    type CancelJobProposal,
} from '../ops/contourJobs';
import {
    setStructureVisibility,
    useStructureVisibilityDeps,
} from '../ops/structureVisibility';
import { goToPosition, useGoToPositionDeps } from '../ops/goToPosition';
import { setWindowLevel, useWindowLevelDeps } from '../ops/windowLevel';
import {
    planSetStructureColor,
    useStructureColorDeps,
    type ColorProposal,
} from '../ops/structureColor';
import {
    planInterpolateStructure,
    useInterpolateStructureDeps,
    type InterpolateProposal,
} from '../ops/interpolateStructure';
import {
    planSmoothStructure,
    useSmoothStructureDeps,
    type SmoothProposal,
} from '../ops/smoothStructure';
import {
    planMoveStructure,
    useMoveStructureDeps,
    type MoveProposal,
} from '../ops/moveStructure';
import {
    planRenameStructure,
    planDeleteStructure,
    planDuplicateStructure,
    planReplaceStructureContours,
    planSetStructureType,
    useStructureManagementDeps,
} from '../ops/structureManagement';
import {
    planCreateMarker,
    planSetMarkerPosition,
    useMarkerDeps,
} from '../ops/markers';
import {
    planCreateStructureSet,
    planEditStructureSetInfo,
    planSetStructureSetApproval,
    useStructureSetDeps,
} from '../ops/structureSetSettings';
import {
    getStructureProperties,
    useStructurePropertiesDeps,
} from '../ops/structureProperties';
import { resetDisplay, useResetDisplayDeps } from '../ops/resetDisplay';
import { showInViewer, useShowInViewerDeps } from '../ops/showInViewer';
import {
    compareStructures,
    listTimepoints,
    planBringPriorContour,
    useBringPriorContourDeps,
    useCompareStructuresDeps,
    useTimepointDeps,
} from '../ops/longitudinal';
import type { ChangeProposal } from '../ops/core/changeProposal';
import {
    planSendStructureSetToPacs,
    useSendStructureSetDeps,
    type SendStructureSetProposal,
} from '../ops/pacsExport';
import type {
    AgentUIMessage,
    ClearContoursOutcome,
    InPlaceEditOutcome,
    JobActionOutcome,
    SendStructureSetOutcome,
    SetStructureColorOutcome,
    ListedStructure,
    LoadSeriesOutcome,
    ModelActionOutcome,
    MutationOutcome,
    StructureChangeOutcome,
} from '../contract';
import {
    useProposalChannel,
    type ProposalChannelView,
    type ChannelControls,
} from './useProposalChannel';
import { repairDanglingToolCalls } from './repairDanglingToolCalls';
import { describeAgentError } from './agentRequest';
import { useAgentRequestHeaders } from './useAgentRequestHeaders';

export type ProposalView = ProposalChannelView<MarginProposal>;
export type RingProposalView = ProposalChannelView<RingProposal>;
export type AsymMarginProposalView = ProposalChannelView<AsymMarginProposal>;
export type BooleanProposalView = ProposalChannelView<BooleanProposal>;
export type ModelProposalView = ProposalChannelView<ModelProposal>;
export type LoadSeriesProposalView = ProposalChannelView<LoadSeriesProposal>;
export type FillCavitiesProposalView =
    ProposalChannelView<FillCavitiesProposal>;
export type RemoveSmallPartsProposalView =
    ProposalChannelView<RemoveSmallPartsProposal>;
export type ClearContoursProposalView =
    ProposalChannelView<ClearContoursProposal>;
export type CancelJobProposalView = ProposalChannelView<CancelJobProposal>;
export type SendStructureSetProposalView =
    ProposalChannelView<SendStructureSetProposal>;
export type ColorProposalView = ProposalChannelView<ColorProposal>;
export type InterpolateProposalView = ProposalChannelView<InterpolateProposal>;
export type SmoothProposalView = ProposalChannelView<SmoothProposal>;
export type MoveProposalView = ProposalChannelView<MoveProposal>;
/** Structure-management cards (rename, delete, type, markers, structure
 *  set …) share one proposal shape, rendered by one card. */
export type ChangeProposalView = ProposalChannelView<ChangeProposal<unknown>>;

/** Card views keyed by tool name then toolCallId — the cross-module shape the
 *  hook returns and the view layer consumes (proposal type erased; the card
 *  registry re-narrows per op). One source for that contract. */
export type ProposalsByTool = Record<
    string,
    Record<string, ProposalChannelView<unknown>>
>;

export function useAgentChat() {
    // Project the current structure set + per-ROI volume into the contract
    // shape for the read tool. A Recoil snapshot keeps the runner React-free
    // and reads the value current at call time (not at render time).
    const readStructures = useRecoilCallback(
        ({ snapshot }) =>
            async (): Promise<ReadonlyArray<ListedStructure>> => {
                // Two independent reads — resolve them together.
                const [structureSet, env] = await Promise.all([
                    snapshot.getPromise(currentStructureSetState),
                    snapshot.getPromise(environmentState),
                ]);
                return projectListedStructures(structureSet, env);
            },
        []
    );

    const makeMarginDeps = useMarginEngineDeps();
    const makeRingDeps = useRingEngineDeps();
    const makeAsymDeps = useAsymMarginEngineDeps();
    const makeBooleanDeps = useBooleanEngineDeps();
    const agentRequestHeaders = useAgentRequestHeaders();
    const makeModelSelectionDeps = useModelSelectionDeps(agentRequestHeaders);
    const makePatientDeps = usePatientDeps();
    const makeFillCavitiesDeps = useFillCavitiesEngineDeps();
    const makeRemoveSmallPartsDeps = useRemoveSmallPartsEngineDeps();
    const makeClearContoursDeps = useClearContoursEngineDeps();
    const makeZoomViewDeps = useZoomViewDeps();
    const makeContourJobDeps = useContourJobDeps();
    const makeSendStructureSetDeps = useSendStructureSetDeps();
    const makeVisibilityDeps = useStructureVisibilityDeps();
    const makeGoToPositionDeps = useGoToPositionDeps();
    const makeWindowLevelDeps = useWindowLevelDeps();
    const makeStructureColorDeps = useStructureColorDeps();
    const makeInterpolateDeps = useInterpolateStructureDeps();
    const makeSmoothDeps = useSmoothStructureDeps();
    const makeMoveDeps = useMoveStructureDeps();
    const makeStructureManagementDeps = useStructureManagementDeps();
    const makeMarkerDeps = useMarkerDeps();
    const makeStructureSetDeps = useStructureSetDeps();
    const makeStructurePropertiesDeps = useStructurePropertiesDeps();
    const makeResetDisplayDeps = useResetDisplayDeps();
    const makeShowInViewerDeps = useShowInViewerDeps();
    const makeTimepointDeps = useTimepointDeps();
    const makeBringPriorDeps = useBringPriorContourDeps();
    const makeCompareDeps = useCompareStructuresDeps();

    // `addToolOutput` only exists after `useChat` returns; a ref breaks the
    // initialiser cycle and keeps the handler identity stable.
    const addToolOutputRef = useRef<(args: AddToolOutputArgs) => void>(
        () => undefined
    );
    const emit = useCallback(
        (args: AddToolOutputArgs) => addToolOutputRef.current(args),
        []
    );

    // One proposal channel per mutating tool; the status machine + the
    // pending-only guard live once in `useProposalChannel`.
    const margin = useProposalChannel<MutationOutcome, MarginProposal>(
        APPLY_SYMMETRIC_MARGIN_TOOL_NAME,
        emit
    );
    const ring = useProposalChannel<MutationOutcome, RingProposal>(
        CREATE_RING_TOOL_NAME,
        emit
    );
    const asym = useProposalChannel<MutationOutcome, AsymMarginProposal>(
        APPLY_ASYMMETRIC_MARGIN_TOOL_NAME,
        emit
    );
    const boolean = useProposalChannel<MutationOutcome, BooleanProposal>(
        BOOLEAN_OPERATION_TOOL_NAME,
        emit
    );
    const proposeModel = useProposalChannel<ModelActionOutcome, ModelProposal>(
        PROPOSE_CONTOUR_MODEL_TOOL_NAME,
        emit
    );
    const startSegmentation = useProposalChannel<
        ModelActionOutcome,
        ModelProposal
    >(START_AUTO_SEGMENTATION_TOOL_NAME, emit);
    const loadSeries = useProposalChannel<
        LoadSeriesOutcome,
        LoadSeriesProposal
    >(PROPOSE_LOAD_SERIES_TOOL_NAME, emit);
    const fillCavities = useProposalChannel<
        InPlaceEditOutcome,
        FillCavitiesProposal
    >(FILL_CAVITIES_TOOL_NAME, emit);
    const removeSmallParts = useProposalChannel<
        InPlaceEditOutcome,
        RemoveSmallPartsProposal
    >(REMOVE_SMALL_PARTS_TOOL_NAME, emit);
    const clearContours = useProposalChannel<
        ClearContoursOutcome,
        ClearContoursProposal
    >(CLEAR_CONTOURS_TOOL_NAME, emit);
    const cancelJob = useProposalChannel<JobActionOutcome, CancelJobProposal>(
        CANCEL_AUTO_SEGMENTATION_TOOL_NAME,
        emit
    );
    const sendStructureSet = useProposalChannel<
        SendStructureSetOutcome,
        SendStructureSetProposal
    >(SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME, emit);
    const structureColor = useProposalChannel<
        SetStructureColorOutcome,
        ColorProposal
    >(SET_STRUCTURE_COLOR_TOOL_NAME, emit);
    const interpolate = useProposalChannel<
        InPlaceEditOutcome,
        InterpolateProposal
    >(INTERPOLATE_STRUCTURE_TOOL_NAME, emit);
    const smooth = useProposalChannel<InPlaceEditOutcome, SmoothProposal>(
        SMOOTH_STRUCTURE_TOOL_NAME,
        emit
    );
    const move = useProposalChannel<InPlaceEditOutcome, MoveProposal>(
        MOVE_STRUCTURE_TOOL_NAME,
        emit
    );
    const renameStructure = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(RENAME_STRUCTURE_TOOL_NAME, emit);
    const deleteStructure = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(DELETE_STRUCTURE_TOOL_NAME, emit);
    const duplicateStructure = useProposalChannel<
        MutationOutcome,
        ChangeProposal<MutationOutcome>
    >(DUPLICATE_STRUCTURE_TOOL_NAME, emit);
    const replaceContours = useProposalChannel<
        InPlaceEditOutcome,
        ChangeProposal<InPlaceEditOutcome>
    >(REPLACE_STRUCTURE_CONTOURS_TOOL_NAME, emit);
    const structureType = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(SET_STRUCTURE_TYPE_TOOL_NAME, emit);
    const createMarker = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(CREATE_MARKER_TOOL_NAME, emit);
    const markerPosition = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(SET_MARKER_POSITION_TOOL_NAME, emit);
    const createStructureSet = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(CREATE_STRUCTURE_SET_TOOL_NAME, emit);
    const structureSetInfo = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(EDIT_STRUCTURE_SET_INFO_TOOL_NAME, emit);
    const structureSetApproval = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(SET_STRUCTURE_SET_APPROVAL_TOOL_NAME, emit);
    const bringPrior = useProposalChannel<
        StructureChangeOutcome,
        ChangeProposal<StructureChangeOutcome>
    >(BRING_PRIOR_CONTOUR_TOOL_NAME, emit);

    // Every gated tool keyed by name. The hooks above stay explicit (Rules
    // of Hooks); the ≤1-pending OR, hover preview, and cancel-on-resend all
    // iterate this one map rather than naming each op repeatedly.
    const channels = useMemo<Record<string, ChannelControls>>(
        () => ({
            [APPLY_SYMMETRIC_MARGIN_TOOL_NAME]: margin,
            [CREATE_RING_TOOL_NAME]: ring,
            [APPLY_ASYMMETRIC_MARGIN_TOOL_NAME]: asym,
            [BOOLEAN_OPERATION_TOOL_NAME]: boolean,
            [PROPOSE_CONTOUR_MODEL_TOOL_NAME]: proposeModel,
            [START_AUTO_SEGMENTATION_TOOL_NAME]: startSegmentation,
            [PROPOSE_LOAD_SERIES_TOOL_NAME]: loadSeries,
            [FILL_CAVITIES_TOOL_NAME]: fillCavities,
            [REMOVE_SMALL_PARTS_TOOL_NAME]: removeSmallParts,
            [CLEAR_CONTOURS_TOOL_NAME]: clearContours,
            [CANCEL_AUTO_SEGMENTATION_TOOL_NAME]: cancelJob,
            [SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME]: sendStructureSet,
            [SET_STRUCTURE_COLOR_TOOL_NAME]: structureColor,
            [INTERPOLATE_STRUCTURE_TOOL_NAME]: interpolate,
            [SMOOTH_STRUCTURE_TOOL_NAME]: smooth,
            [MOVE_STRUCTURE_TOOL_NAME]: move,
            [RENAME_STRUCTURE_TOOL_NAME]: renameStructure,
            [DELETE_STRUCTURE_TOOL_NAME]: deleteStructure,
            [DUPLICATE_STRUCTURE_TOOL_NAME]: duplicateStructure,
            [REPLACE_STRUCTURE_CONTOURS_TOOL_NAME]: replaceContours,
            [SET_STRUCTURE_TYPE_TOOL_NAME]: structureType,
            [CREATE_MARKER_TOOL_NAME]: createMarker,
            [SET_MARKER_POSITION_TOOL_NAME]: markerPosition,
            [CREATE_STRUCTURE_SET_TOOL_NAME]: createStructureSet,
            [EDIT_STRUCTURE_SET_INFO_TOOL_NAME]: structureSetInfo,
            [SET_STRUCTURE_SET_APPROVAL_TOOL_NAME]: structureSetApproval,
            [BRING_PRIOR_CONTOUR_TOOL_NAME]: bringPrior,
        }),
        [
            margin,
            ring,
            asym,
            boolean,
            proposeModel,
            startSegmentation,
            loadSeries,
            fillCavities,
            removeSmallParts,
            clearContours,
            cancelJob,
            sendStructureSet,
            structureColor,
            interpolate,
            smooth,
            move,
            renameStructure,
            deleteStructure,
            duplicateStructure,
            replaceContours,
            structureType,
            createMarker,
            markerPosition,
            createStructureSet,
            structureSetInfo,
            structureSetApproval,
            bringPrior,
        ]
    );

    // ≤1-pending spans EVERY mutating tool: a pending proposal in ANY channel
    // blocks a new proposal in any channel. The cross-op OR stays at this level.
    const pendingExists = useMemo(
        () =>
            Object.values(channels).some((ch) =>
                Object.values(ch.views).some(
                    (p) => p.status === 'pending' || p.status === 'applying'
                )
            ),
        [channels]
    );
    const pendingRef = useRef(pendingExists);
    pendingRef.current = pendingExists;

    // Cancel every still-pending proposal when the active structure set
    // changes: its eagerly-computed geometry belongs to the previous set's
    // frame and must not be applied into the new one. A ref holds the latest
    // canceller so the effect keys on the set id ALONE — depending on
    // `channels` would cancel a proposal the instant it registers.
    const cancelPendingForContextChange = useRef<() => void>(() => undefined);
    cancelPendingForContextChange.current = () => {
        for (const ch of Object.values(channels)) {
            for (const [id, view] of Object.entries(ch.views)) {
                if (view.status === 'pending') {
                    ch.cancel(
                        id,
                        'cancelled — the active structure set changed'
                    );
                }
            }
        }
    };
    // The reactive twin of commit.currentContextId — the same structure-set
    // UID, read via Recoil so a switch re-renders and fires the effect below.
    const activeStructureSetId = useRecoilValue(
        currentStructureSetState
    )?.dicomUid;
    useEffect(() => {
        cancelPendingForContextChange.current();
    }, [activeStructureSetId]);

    // Stateless transport — construct once (the drawer re-renders per token).
    const transportRef = useRef<DefaultChatTransport<AgentUIMessage>>();
    if (!transportRef.current) {
        transportRef.current = new DefaultChatTransport({
            api: AppConfig.agentChatUrl,
            // Resolved per request: the sign-in token and, in test-file
            // mode, the test user (app-config.json "agentTestUser").
            headers: agentRequestHeaders,
        });
    }

    // One gate per mutating tool, keyed by name: each plan closure binds the
    // live engine deps + the current ≤1-pending flag; `mutatingOpHandler` ties
    // it to the channel's register, keeping the proposal type encapsulated.
    const mutatingOps = useMemo<Record<string, MutatingOpHandler>>(
        () => ({
            [APPLY_SYMMETRIC_MARGIN_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planMarginToolCall(c, {
                        ...makeMarginDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                margin.register
            ),
            [CREATE_RING_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planRingToolCall(c, {
                        ...makeRingDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                ring.register
            ),
            [APPLY_ASYMMETRIC_MARGIN_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planAsymMarginToolCall(c, {
                        ...makeAsymDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                asym.register
            ),
            [BOOLEAN_OPERATION_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planBooleanToolCall(c, {
                        ...makeBooleanDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                boolean.register
            ),
            [PROPOSE_CONTOUR_MODEL_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planProposeContourModel(c, {
                        ...makeModelSelectionDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                proposeModel.register
            ),
            [START_AUTO_SEGMENTATION_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planStartAutoSegmentation(c, {
                        ...makeModelSelectionDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                startSegmentation.register
            ),
            [PROPOSE_LOAD_SERIES_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planProposeLoadSeries(c, {
                        ...makePatientDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                loadSeries.register
            ),
            [FILL_CAVITIES_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planFillCavitiesToolCall(c, {
                        ...makeFillCavitiesDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                fillCavities.register
            ),
            [REMOVE_SMALL_PARTS_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planRemoveSmallPartsToolCall(c, {
                        ...makeRemoveSmallPartsDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                removeSmallParts.register
            ),
            [CLEAR_CONTOURS_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planClearContoursToolCall(c, {
                        ...makeClearContoursDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                clearContours.register
            ),
            [CANCEL_AUTO_SEGMENTATION_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planCancelAutoSegmentation(c, {
                        ...makeContourJobDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                cancelJob.register
            ),
            [SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planSendStructureSetToPacs(c, {
                        ...makeSendStructureSetDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                sendStructureSet.register
            ),
            [SET_STRUCTURE_COLOR_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planSetStructureColor(c, {
                        ...makeStructureColorDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                structureColor.register
            ),
            [INTERPOLATE_STRUCTURE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planInterpolateStructure(c, {
                        ...makeInterpolateDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                interpolate.register
            ),
            [SMOOTH_STRUCTURE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planSmoothStructure(c, {
                        ...makeSmoothDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                smooth.register
            ),
            [MOVE_STRUCTURE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planMoveStructure(c, {
                        ...makeMoveDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                move.register
            ),
            [RENAME_STRUCTURE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planRenameStructure(c, {
                        ...makeStructureManagementDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                renameStructure.register
            ),
            [DELETE_STRUCTURE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planDeleteStructure(c, {
                        ...makeStructureManagementDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                deleteStructure.register
            ),
            [DUPLICATE_STRUCTURE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planDuplicateStructure(c, {
                        ...makeStructureManagementDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                duplicateStructure.register
            ),
            [REPLACE_STRUCTURE_CONTOURS_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planReplaceStructureContours(c, {
                        ...makeStructureManagementDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                replaceContours.register
            ),
            [SET_STRUCTURE_TYPE_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planSetStructureType(c, {
                        ...makeStructureManagementDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                structureType.register
            ),
            [CREATE_MARKER_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planCreateMarker(c, {
                        ...makeMarkerDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                createMarker.register
            ),
            [SET_MARKER_POSITION_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planSetMarkerPosition(c, {
                        ...makeMarkerDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                markerPosition.register
            ),
            [CREATE_STRUCTURE_SET_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planCreateStructureSet(c, {
                        ...makeStructureSetDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                createStructureSet.register
            ),
            [EDIT_STRUCTURE_SET_INFO_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planEditStructureSetInfo(c, {
                        ...makeStructureSetDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                structureSetInfo.register
            ),
            [SET_STRUCTURE_SET_APPROVAL_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planSetStructureSetApproval(c, {
                        ...makeStructureSetDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                structureSetApproval.register
            ),
            [BRING_PRIOR_CONTOUR_TOOL_NAME]: mutatingOpHandler(
                (c) =>
                    planBringPriorContour(c, {
                        ...makeBringPriorDeps(),
                        pendingMutationExists: pendingRef.current,
                    }),
                bringPrior.register
            ),
        }),
        [
            makeMarginDeps,
            makeRingDeps,
            makeAsymDeps,
            makeBooleanDeps,
            makeModelSelectionDeps,
            makePatientDeps,
            makeFillCavitiesDeps,
            makeRemoveSmallPartsDeps,
            makeClearContoursDeps,
            makeContourJobDeps,
            makeSendStructureSetDeps,
            makeStructureColorDeps,
            makeInterpolateDeps,
            makeSmoothDeps,
            makeMoveDeps,
            makeStructureManagementDeps,
            makeMarkerDeps,
            makeStructureSetDeps,
            makeBringPriorDeps,
            margin.register,
            ring.register,
            asym.register,
            boolean.register,
            proposeModel.register,
            startSegmentation.register,
            loadSeries.register,
            fillCavities.register,
            removeSmallParts.register,
            clearContours.register,
            cancelJob.register,
            sendStructureSet.register,
            structureColor.register,
            interpolate.register,
            smooth.register,
            move.register,
            renameStructure.register,
            deleteStructure.register,
            duplicateStructure.register,
            replaceContours.register,
            structureType.register,
            createMarker.register,
            markerPosition.register,
            createStructureSet.register,
            structureSetInfo.register,
            structureSetApproval.register,
            bringPrior.register,
        ]
    );

    // Extra read-only tools: auto-run with no human gate, like list_structures.
    const readOps = useMemo<Record<string, ReadOpHandler>>(
        () => ({
            [ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME]: () =>
                analyzeImageForModelSelection(makeModelSelectionDeps()),
            [SEARCH_PATIENTS_TOOL_NAME]: (c) =>
                searchPatients(c.input, makePatientDeps()),
            [LIST_PATIENT_STUDIES_TOOL_NAME]: (c) =>
                listPatientStudies(c.input, makePatientDeps()),
            [LIST_IMPORTED_PATIENTS_TOOL_NAME]: (c) =>
                listImportedPatients(c.input, makePatientDeps()),
            [CHECK_PACS_CONNECTION_TOOL_NAME]: () =>
                checkPacsConnection(makePatientDeps()),
            // View-only (never touches contours), so auto-run like a read.
            [ZOOM_VIEW_TOOL_NAME]: async (c) =>
                zoomView(c.input, makeZoomViewDeps()),
            [CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME]: (c) =>
                checkAutoSegmentationJobs(c.input, makeContourJobDeps()),
            // View-only display tools: auto-run like reads.
            [SET_STRUCTURE_VISIBILITY_TOOL_NAME]: async (c) =>
                setStructureVisibility(c.input, makeVisibilityDeps()),
            [GO_TO_POSITION_TOOL_NAME]: async (c) =>
                goToPosition(c.input, makeGoToPositionDeps()),
            [SET_WINDOW_LEVEL_TOOL_NAME]: async (c) =>
                setWindowLevel(c.input, makeWindowLevelDeps()),
            [GET_STRUCTURE_PROPERTIES_TOOL_NAME]: async (c) =>
                getStructureProperties(c.input, makeStructurePropertiesDeps()),
            [RESET_DISPLAY_TOOL_NAME]: async (c) =>
                resetDisplay(c.input, makeResetDisplayDeps()),
            // Only points at a control; never clicks or changes anything.
            [SHOW_IN_VIEWER_TOOL_NAME]: async (c) =>
                showInViewer(c.input, makeShowInViewerDeps()),
            [LIST_TIMEPOINTS_TOOL_NAME]: async () =>
                listTimepoints(makeTimepointDeps()),
            // Deterministic numbers from the viewer's own calculations.
            [COMPARE_STRUCTURES_TOOL_NAME]: async (c) =>
                compareStructures(c.input, makeCompareDeps()),
        }),
        [
            makeModelSelectionDeps,
            makePatientDeps,
            makeZoomViewDeps,
            makeContourJobDeps,
            makeVisibilityDeps,
            makeGoToPositionDeps,
            makeWindowLevelDeps,
            makeStructurePropertiesDeps,
            makeResetDisplayDeps,
            makeShowInViewerDeps,
            makeTimepointDeps,
            makeCompareDeps,
        ]
    );

    const onToolCall = useCallback(
        ({ toolCall }: { toolCall: AgentToolCall }) =>
            handleAgentToolCall({
                toolCall: {
                    toolName: toolCall.toolName,
                    toolCallId: toolCall.toolCallId,
                    input: toolCall.input,
                },
                addToolOutput: emit,
                structures: readStructures,
                mutatingOps,
                readOps,
            }),
        [readStructures, emit, mutatingOps, readOps]
    );

    const chat = useChat<AgentUIMessage>({
        transport: transportRef.current,
        // Batch streamed updates: without this every SSE part re-renders the
        // whole drawer at token rate.
        experimental_throttle: 50,
        // Resume the loop once a tool output is supplied (read auto-output, or
        // the clinician's Apply/Reject outcome). A pending proposal supplies
        // NO output, so the loop stays halted at the HITL gate.
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
        onToolCall,
    });
    addToolOutputRef.current = (args) =>
        chat.addToolOutput(args as Parameters<typeof chat.addToolOutput>[0]);

    const sendText = useCallback(
        (text: string) => {
            const trimmed = text.trim();
            if (trimmed.length === 0) {
                return;
            }
            // Repair BEFORE appending the user message (the SDK can only
            // settle parts on the last message) and via setMessages —
            // addToolOutput would fire an automatic resume of its own here.
            // An in-flight Apply is never repaired: its commit may succeed,
            // so claiming failure for it would be a lie.
            const applying = new Set<string>();
            for (const ch of Object.values(channels)) {
                for (const [id, view] of Object.entries(ch.views)) {
                    if (view.status === 'applying') {
                        applying.add(id);
                    }
                }
            }
            const repair = repairDanglingToolCalls(chat.messages, applying);
            if (repair.repaired) {
                chat.setMessages(repair.messages);
                // Sync the cards: a repaired call's proposal can no longer
                // be Applied — the history already says it was cancelled.
                for (const id of repair.repairedCallIds) {
                    for (const ch of Object.values(channels)) {
                        ch.cancel(id);
                    }
                }
            }
            void chat.sendMessage({ text: trimmed });
        },
        [chat, channels]
    );

    // Card hover preview: a direct keyed lookup like apply/reject — the caller
    // (which renders the card) always knows the tool name.
    const showProposalPreview = useCallback(
        (toolName: string, toolCallId: string) => {
            channels[toolName]?.showPreview(toolCallId);
        },
        [channels]
    );
    const hideProposalPreview = useCallback(
        (toolName: string, toolCallId: string) => {
            channels[toolName]?.hidePreview(toolCallId);
        },
        [channels]
    );

    // The card views + Apply/Reject, keyed by tool name. The view layer looks
    // up `proposalsByTool[toolName]` and calls apply/reject with the same name;
    // adding an op surfaces here automatically (no new return fields).
    const proposalsByTool = useMemo<ProposalsByTool>(
        () =>
            Object.fromEntries(
                Object.entries(channels).map(([tool, ch]) => [tool, ch.views])
            ),
        [channels]
    );
    const applyProposal = useCallback(
        (toolName: string, toolCallId: string): Promise<void> =>
            channels[toolName]?.apply(toolCallId) ?? Promise.resolve(),
        [channels]
    );
    const rejectProposal = useCallback(
        (toolName: string, toolCallId: string) => {
            channels[toolName]?.reject(toolCallId);
        },
        [channels]
    );

    return {
        messages: chat.messages,
        status: chat.status,
        /** One readable line when the agent server refused or failed. */
        errorText: describeAgentError(chat.error),
        sendText,
        stop: chat.stop,
        proposalsByTool,
        applyProposal,
        rejectProposal,
        showProposalPreview,
        hideProposalPreview,
    };
}

interface AgentToolCall {
    readonly toolName: string;
    readonly toolCallId: string;
    readonly input: unknown;
}
